import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {TablePerformanceSummary} from 'notebook/performance/tableLimitUtils';
import {GRAY_LIGHT} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';

export interface TablePerformanceSummaryBarProps {
  limitThreshold: number;
  numCells: number;
  tableSummaries: Array<TablePerformanceSummary>;
  focusedTableId: string | null;
  onHover: (tableId: string) => void;
  onLeave: () => void;
}

function formatPercentage(value: number): string {
  const percentage = value * 100;
  return percentage > 0 && percentage < 1 ? '<1%' : `${percentage.toFixed(0)}%`;
}

export function TablePerformanceSummaryBar({
  limitThreshold,
  numCells,
  tableSummaries,
  focusedTableId,
  onHover,
  onLeave,
}: TablePerformanceSummaryBarProps): JSX.Element {
  const sortedSummaries = tableSummaries
    .filter((table) => table.percentage > 0)
    .sort((a, b) => a.percentage - b.percentage);

  return (
    <div className={css(styles.outerFlexContainer)}>
      <div className={css(styles.leftSideContainer)}>
        <div className={css(styles.barWrapper, styles.roundedLeftSide, styles.roundedRightSide)}>
          {sortedSummaries.map((tableSummary, index) => {
            const {colorHex, percentage, name, tableId} = tableSummary;
            const style = {
              backgroundColor: colorHex,
              width: `${percentage * 100}%`,
            };
            return (
              <Tooltip text={`${name} - ${formatPercentage(percentage)}`} key={index}>
                <div
                  style={style}
                  className={css(
                    styles.categorySection,
                    tableId !== focusedTableId ? styles.blurredCategory : undefined,
                    index === 0 ? styles.roundedLeftSide : undefined,
                    index === sortedSummaries.length - 1 ? styles.roundedRightSide : undefined
                  )}
                  onMouseEnter={(): void => {
                    if (onHover) {
                      onHover(tableId);
                    }
                  }}
                  onMouseLeave={(): void => {
                    if (onLeave) {
                      onLeave();
                    }
                  }}
                />
              </Tooltip>
            );
          })}
        </div>
        <div className={css(styles.totalSize)}>
          Table size limit: {formatPercentage(numCells / limitThreshold)}
        </div>
      </div>
    </div>
  );
}

const topPadding = 8;
const barHeight = 32;
const bottomPadding = 20;

const styles = StyleSheet.create({
  outerFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  leftSideContainer: {
    position: 'relative',
    paddingTop: `${topPadding}px`,
    paddingBottom: `${bottomPadding}px`,
    flexGrow: 1,
  },
  barWrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: `${barHeight}px`,
    border: `1px solid ${GRAY_LIGHT}`,
    position: 'relative',
    overflow: 'hidden',
  },
  roundedLeftSide: {
    borderTopLeftRadius: `${topPadding}px`,
    borderBottomLeftRadius: `${topPadding}px`,
  },
  roundedRightSide: {
    borderTopRightRadius: `${topPadding}px`,
    borderBottomRightRadius: `${topPadding}px`,
  },
  categorySection: {
    height: '100%',
  },
  blurredCategory: {
    opacity: 0.5,
  },
  totalSize: {
    position: 'absolute',
    left: 0,
    bottom: 0,
  },
});
